import { useLayoutEffect, useMemo, useRef } from 'react';
import * as THREE from 'three';
// Relative imports (not `@/`) and no hexmap index so bun can test this module.
import config from '@/game/hexmap/config';
import { axialToWorld, worldToAxial } from '@/game/hexmap/coords';
import { getHexTileMapKey } from '@/game/hexmap/model/keys';
import { axialNeighbors } from '@/game/hexmap/model/map';
import type { HexTileMapKey } from '@/game/hexmap/model/types';
import type { Building, PlayerId } from '@/game/sim';
import type { Frame } from '@/game/view/frame';
import { borderEdgeShown, knownTerritories } from '@/game/view/lastSeen';
import { colorIndexFor, PLAYER_COLORS } from '@/game/view/palette';
import { useSimSelector } from './app/simFrame';

/**
 * Coloured border around each player's territory (the city discs where farms
 * can be placed). One InstancedMesh of thin bars, one bar per boundary hex
 * edge, plus a round joint on every corner so the bars read as one
 * continuous line. Each line runs just inside its owner's land, so where two
 * territories meet both lines show side by side. Own borders always show;
 * rivals' only on tiles the viewer has seen.
 */

const RADIUS = config.hexMap.tile.radius;
const BAR_Y = config.hexMap.tile.thickness;
export const BAR_WIDTH = 0.1;
const BAR_HEIGHT = 0.04;
/** How far a line sits inside its owner's edge: its outer side on the edge. */
const INSET = BAR_WIDTH / 2;
/** A corner's inset point lies this far from it (INSET / sin 60°). */
const CORNER_SHIFT = (2 * INSET) / Math.sqrt(3);
// A hex edge is exactly as long as the hex radius (bars are scaled to fit
// their inset ends); joints cover the corners.
const barGeometry = new THREE.BoxGeometry(RADIUS, BAR_HEIGHT, BAR_WIDTH);
const jointGeometry = new THREE.CylinderGeometry(BAR_WIDTH / 2, BAR_WIDTH / 2, BAR_HEIGHT, 12);
const barMaterial = new THREE.MeshBasicMaterial({ color: 'white' });
const MAX_BARS = 4096;
const noRaycast = () => null;

export interface BorderBar {
  owner: PlayerId;
  x: number;
  z: number;
  /** Turn about the y axis of the bar's length (its local x axis). */
  angle: number;
  length: number;
}

export interface BorderJoint {
  owner: PlayerId;
  x: number;
  z: number;
}

/**
 * Bars (one per boundary hex edge) and corner joints outlining each player's
 * drawn territory, in `order`. `shown(owner, key, nbKey)` says whether to
 * draw the edge between that tile and its neighbour (`borderEdgeShown`).
 *
 * Each line is inset by half its width, so it lies inside its owner's land
 * with its outer side on the edge: two territories sharing an edge get two
 * lines side by side, rather than one hiding the other and breaking up that
 * player's outline. At a corner the inset point moves towards the owner's
 * side of it: towards the tile's centre where the outline turns round the
 * tile (convex), away from the outside tile's centre where it bends round it
 * (concave; the third tile at the corner is the owner's too). Consecutive
 * bars share their corner points exactly, so each outline stays one closed
 * line.
 */
export function borderGeometry(
  drawn: ReadonlyMap<PlayerId, ReadonlySet<HexTileMapKey>>,
  order: readonly PlayerId[],
  shown: (owner: PlayerId, key: HexTileMapKey, nbKey: HexTileMapKey) => boolean
): { bars: BorderBar[]; joints: BorderJoint[] } {
  const bars: BorderBar[] = [];
  const joints = new Map<string, BorderJoint>();
  for (const owner of order) {
    const territory = drawn.get(owner);
    if (!territory) continue;
    for (const key of territory) {
      const [q, r] = key.split(',').map(Number);
      const center = axialToWorld({ q, r }, RADIUS);
      for (const nb of axialNeighbors({ q, r })) {
        const nbKey = getHexTileMapKey(nb);
        if (territory.has(nbKey) || !shown(owner, key, nbKey)) continue;
        const other = axialToWorld(nb, RADIUS);
        const dx = other.x - center.x;
        const dz = other.z - center.z;
        const len = Math.hypot(dx, dz);
        // The edge's two corners, half an edge either side of its midpoint.
        const mx = (center.x + other.x) / 2;
        const mz = (center.z + other.z) / 2;
        const ux = (-dz / len) * (RADIUS / 2);
        const uz = (dx / len) * (RADIUS / 2);
        const [a, b] = [
          [mx + ux, mz + uz],
          [mx - ux, mz - uz],
        ].map(([cx, cz]) => {
          // The third tile at the corner (its centre and this edge's two
          // tiles' centres average to the corner).
          const third = getHexTileMapKey(
            worldToAxial(3 * cx - center.x - other.x, 3 * cz - center.z - other.z, RADIUS)
          );
          const concave = territory.has(third);
          const from = concave ? other : center;
          const sign = concave ? -1 : 1;
          const joint = {
            owner,
            x: cx + (sign * (from.x - cx) * CORNER_SHIFT) / RADIUS,
            z: cz + (sign * (from.z - cz) * CORNER_SHIFT) / RADIUS,
          };
          // One joint per owner and corner (named by its three tiles), so
          // floating-point noise never doubles one up.
          const cornerKey = `${owner}:${[key, nbKey, third].sort().join('|')}`;
          if (!joints.has(cornerKey)) joints.set(cornerKey, joint);
          return joint;
        });
        bars.push({
          owner,
          x: (a.x + b.x) / 2,
          z: (a.z + b.z) / 2,
          // Edge runs perpendicular to the centre-to-neighbour direction.
          angle: -Math.atan2(dz, dx) + Math.PI / 2,
          length: Math.hypot(a.x - b.x, a.z - b.z),
        });
      }
    }
  }
  return { bars, joints: [...joints.values()] };
}

/** What the borders are drawn from, and the key that says when they change. */
interface TerritoryInput {
  frame: Frame;
  known: Building[];
  signature: string;
}

/**
 * Territory only changes when a territory-giving building completes, levels
 * up or is destroyed, so the (relatively costly) edge rebuild is keyed on
 * that, and on the fog. Own borders are live, and so is every tile in sight
 * (it's what shelters the units standing there) and the whole territory of
 * every rival city the viewer has seen (`knownBuildings`), so its growth or
 * fall shows at once; a city never seen shows only on land in sight. All of
 * them come from one `knownTerritories` pass, which gives each tile at most
 * one owner, so borders never overlap.
 */
function territoryInput(frame: Frame): TerritoryInput {
  const { view: state, rules } = frame;
  const known = frame.knownBuildings();
  const territoryKey = Object.values(state.buildings)
    .filter((b) => b.buildProgress >= 1 && rules.buildings[b.type]?.territoryRadius !== undefined)
    .map((b) => `${b.id}@${b.pos.q},${b.pos.r}:${b.level ?? 1}`)
    .sort()
    .join('|');
  // Live cities shape every border; which rival cities have been seen decides
  // which of theirs show out of sight.
  const seenKey = known
    .filter((b) => rules.buildings[b.type]?.territoryRadius !== undefined)
    .map((b) => b.id)
    .sort()
    .join('|');
  return { frame, known, signature: `${territoryKey}#${seenKey}` };
}

const sameTerritory = (a: TerritoryInput, b: TerritoryInput) =>
  a.signature === b.signature && a.frame.fog === b.frame.fog;

export function TerritoryLayer() {
  const meshRef = useRef<THREE.InstancedMesh>(null);
  const jointRef = useRef<THREE.InstancedMesh>(null);
  const input = useSimSelector(territoryInput, sameTerritory);

  const { bars, joints } = useMemo(() => {
    const { frame, known } = input;
    const { view: state, playerId, playerOrder, rules } = frame;
    const sight = frame.fog.sight;
    const drawn = knownTerritories(state, known, playerId, rules, sight);
    const geometry = borderGeometry(
      drawn,
      playerOrder,
      borderEdgeShown(state, known, drawn, playerId, rules, sight)
    );
    const colors = new Map(
      playerOrder.map((p) => [p, new THREE.Color(PLAYER_COLORS[colorIndexFor(p, playerOrder)])])
    );
    const color = (owner: PlayerId) => colors.get(owner) ?? new THREE.Color('white');
    return {
      bars: geometry.bars.slice(0, MAX_BARS).map((bar) => ({ ...bar, color: color(bar.owner) })),
      joints: geometry.joints
        .slice(0, MAX_BARS)
        .map((joint) => ({ ...joint, color: color(joint.owner) })),
    };
  }, [input]);

  useLayoutEffect(() => {
    const mesh = meshRef.current;
    if (!mesh) return;
    const dummy = new THREE.Object3D();
    bars.forEach((bar, i) => {
      dummy.position.set(bar.x, BAR_Y, bar.z);
      dummy.rotation.set(0, bar.angle, 0);
      dummy.scale.set(bar.length / RADIUS, 1, 1);
      dummy.updateMatrix();
      mesh.setMatrixAt(i, dummy.matrix);
      mesh.setColorAt(i, bar.color);
    });
    mesh.count = bars.length;
    mesh.instanceMatrix.needsUpdate = true;
    if (mesh.instanceColor) mesh.instanceColor.needsUpdate = true;

    const jointMesh = jointRef.current;
    if (!jointMesh) return;
    dummy.rotation.set(0, 0, 0);
    dummy.scale.set(1, 1, 1);
    joints.forEach((joint, i) => {
      dummy.position.set(joint.x, BAR_Y, joint.z);
      dummy.updateMatrix();
      jointMesh.setMatrixAt(i, dummy.matrix);
      jointMesh.setColorAt(i, joint.color);
    });
    jointMesh.count = joints.length;
    jointMesh.instanceMatrix.needsUpdate = true;
    if (jointMesh.instanceColor) jointMesh.instanceColor.needsUpdate = true;
  }, [bars, joints]);

  return (
    <>
      <instancedMesh
        ref={meshRef}
        args={[barGeometry, barMaterial, MAX_BARS]}
        frustumCulled={false}
        raycast={noRaycast}
      />
      <instancedMesh
        ref={jointRef}
        args={[jointGeometry, barMaterial, MAX_BARS]}
        frustumCulled={false}
        raycast={noRaycast}
      />
    </>
  );
}
